import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const getResultColor = (result) => {
    switch (result?.toLowerCase()) {
        case 'passed':
            return 'bg-green-100 text-green-800 border-green-300';
        case 'failed':
            return 'bg-red-100 text-red-800 border-red-300';
        case 'conditional':
            return 'bg-yellow-100 text-yellow-800 border-yellow-300';
        case 'invalid':
            return 'bg-red-700 text-white border-red-800';
        default:
            return 'bg-gray-200 text-gray-900 border-gray-400';
    }
};

const fallbackFormatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
        return 'N/A';
    }
};

const InspectionsPanel = ({ plateNo, inspections = [], loading = false, formatDate }) => {
    const fmt = formatDate || fallbackFormatDate;

    return (
        <div>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
                <h3 className="text-base font-bold text-[#2a2e30]">Inspection History</h3>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                    <Link
                        to={`/inspections/internal?plate=${encodeURIComponent(plateNo)}`}
                        className="w-full sm:w-auto text-center px-3 py-2 bg-[#FF5733] text-white text-sm font-semibold rounded-sm hover:bg-[#e64a2b] transition-all duration-200"
                    >
                        View All Internal
                    </Link>
                    <Link
                        to={`/inspections/government?plate=${encodeURIComponent(plateNo)}`}
                        className="w-full sm:w-auto text-center px-3 py-2 bg-[#FF5733] text-white text-sm font-semibold rounded-sm hover:bg-[#e64a2b] transition-all duration-200"
                    >
                        View All Government
                    </Link>
                </div>
            </div>

            {loading ? (
                <div className="flex justify-center items-center py-12">
                    <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-[#FF5733]" />
                </div>
            ) : (
                <div className="space-y-6">
                    {/* Government Inspections */}
                    <div>
                        <h4 className="text-sm font-bold text-[#2a2e30] mb-4 uppercase tracking-wide">Government Inspections</h4>
                        {inspections.filter(inv => inv.inspection_type === 'government').length > 0 ? (
                            <div>
                                <div className="md:hidden space-y-3 mb-4">
                                    {inspections
                                        .filter(inv => inv.inspection_type === 'government')
                                        .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date))
                                        .map(inspection => (
                                            <div key={inspection.id} className="bg-white p-4 rounded-sm shadow-none border border-gray-100">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-sm text-gray-500">{fmt(inspection.inspection_date)}</div>
                                                        <div className="mt-1 font-medium text-gray-800">{inspection.inspector_name || inspection.inspector || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <span className={`px-3 py-1 rounded-sm text-xs font-bold border ${getResultColor(inspection.result)}`}>{inspection.result || 'pending'}</span>
                                                    </div>
                                                </div>
                                                {inspection.notes ? <div className="mt-2 text-sm text-gray-700">{inspection.notes}</div> : null}
                                            </div>
                                        ))}
                                </div>

                                <div className="hidden md:overflow-x-auto md:block">
                                    <div className="border border-gray-300 rounded-sm overflow-hidden">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-24">Date</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-20">Result</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-52 max-w-[13.5rem]">Inspector</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none max-w-[10rem]">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inspections
                                                .filter(inv => inv.inspection_type === 'government')
                                                .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date))
                                                .map((inspection, idx) => (
                                                    <tr key={inspection.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700 w-24">{fmt(inspection.inspection_date)}</td>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs w-20"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold border ${getResultColor(inspection.result)}`}>{inspection.result || 'pending'}</span></td>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700 w-52 max-w-[13.5rem] truncate" title={inspection.inspector_name || inspection.inspector || ''}>{inspection.inspector_name || inspection.inspector || 'N/A'}</td>
                                                        <td className="px-2 py-2 text-xs text-gray-700 max-w-[10rem] truncate" title={inspection.notes || ''}>{inspection.notes ? inspection.notes.substring(0, 50) + (inspection.notes.length > 50 ? '...' : '') : 'N/A'}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                                <p className="text-gray-500">No government inspections found.</p>
                            </div>
                        )}
                    </div>

                    {/* Internal Inspections */}
                    <div>
                        <h4 className="text-sm font-bold text-[#2a2e30] mb-4 uppercase tracking-wide">Internal Inspections</h4>
                        {inspections.filter(inv => inv.inspection_type === 'internal').length > 0 ? (
                            <div>
                                <div className="md:hidden space-y-3 mb-4">
                                    {inspections
                                        .filter(inv => inv.inspection_type === 'internal')
                                        .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date))
                                        .map(inspection => (
                                            <div key={inspection.id} className="bg-white p-4 rounded-sm shadow-none border border-gray-100">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="text-sm text-gray-500">{fmt(inspection.inspection_date)}</div>
                                                        <div className="mt-1 font-medium text-gray-800">{inspection.inspector_name || inspection.inspector || 'N/A'}</div>
                                                    </div>
                                                    <div>
                                                        <span className={`px-3 py-1 rounded-sm text-xs font-bold border ${getResultColor(inspection.result)}`}>{inspection.result || 'pending'}</span>
                                                    </div>
                                                </div>
                                                {inspection.notes ? <div className="mt-2 text-sm text-gray-700">{inspection.notes}</div> : null}
                                            </div>
                                        ))}
                                </div>

                                <div className="hidden md:overflow-x-auto md:block">
                                    <div className="border border-gray-300 rounded-sm overflow-hidden">
                                    <table className="min-w-full">
                                        <thead>
                                            <tr className="border-b border-blue-gray-100 bg-[#EDE4DA]">
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-24">Date</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-20">Result</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none w-52 max-w-[13.5rem]">Inspector</th>
                                                <th className="text-left px-2 py-2 text-xs font-semibold text-gray-800 leading-none max-w-[10rem]">Notes</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {inspections
                                                .filter(inv => inv.inspection_type === 'internal')
                                                .sort((a, b) => new Date(b.inspection_date) - new Date(a.inspection_date))
                                                .map((inspection, idx) => (
                                                    <tr key={inspection.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors duration-150`}>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700 w-24">{fmt(inspection.inspection_date)}</td>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs w-20"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold border ${getResultColor(inspection.result)}`}>{inspection.result || 'pending'}</span></td>
                                                        <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-700 w-52 max-w-[13.5rem] truncate" title={inspection.inspector_name || inspection.inspector || ''}>{inspection.inspector_name || inspection.inspector || 'N/A'}</td>
                                                        <td className="px-2 py-2 text-xs text-gray-700 max-w-[10rem] truncate" title={inspection.notes || ''}>{inspection.notes ? inspection.notes.substring(0, 50) + (inspection.notes.length > 50 ? '...' : '') : 'N/A'}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-12 bg-gray-50 rounded-lg">
                                <p className="text-gray-500">No internal inspections found.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default InspectionsPanel;
